import React, { useEffect, useState } from 'react'
import PageLoader from '../../shared/PageLoader'
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { Box, Button, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from '@mui/material';
import { FaDownload } from "react-icons/fa";
import { useExportEmployeesWage } from '../../../backend/exports';
import { downloadFileContent } from '../../../utils/common';
import { ERROR_MESSAGES } from '../../../utils/constants';
import { toast } from 'react-toastify';
import { bulkDeleteEmployeeWage, getEmployeesWage } from '../../../redux/features/employeeMaster.slice';
import EmployeeUploadFilters from '../../common/EmployeeUploadFilters';
import AdditionalFiltersInputUploads from '../../common/AdditionalFIltersInputUploads';
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  useEmployeeUploadCompanyData
} from '../../../hooks/useEmployeeUploads';
import {
  formatEmployeeUploadDate,
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload
} from '../../../utils/employeeUploads.utils';

const style = {
  position: 'absolute' as 'absolute',
  top: '25%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

const EmployeeWageUpload = () => {
  const dispatch = useAppDispatch();

  // Redux selectors
  const employeesWageDetails = useAppSelector((state) => state.employeeMaster.employeesWageDetails);

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } = useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig();
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    monthFieldName: 'month_eq',
    searchFieldName: 'employee_code_cont'
  });
  const {
    companies,
    associateCompanies,
    locations,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations
  } = useEmployeeUploadCompanyData();

  // Local state for UI components
  const [companyName, setCompanyName] = useState('');
  const [associate_company_name, setAssociateCompanyName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState('');
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] = useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useState<any>({});

  const employeesWage = employeesWageDetails.data.items || [];
  const employeesWageCount = employeesWageDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployeesWage((response: any) => {
    let filename = associate_company_name + '_' + locationName + '_' + 'Employee-Wage' + '_' + filters.year + '_' + filters.month + '.xlsx';
    downloadFileContent({
      name: filename,
      type: response.headers['content-type'],
      content: response.data
    });
  }, () => {
    toast.error(ERROR_MESSAGES.DEFAULT);
  });

  const loading = exporting || employeesWageDetails.status === 'loading';

  // Data fetching effects
  useEffect(() => {
    fetchCompanies();
    fetchData(getEmployeesWage, filters, searchConfig);
  }, []);

  // Fetch data when filters change (including additional filters)
  useEffect(() => {
    if (filters.establishmentType || filters.company || filters.associateCompany || filters.location || filters.year || filters.month) {
      fetchData(getEmployeesWage, filters, searchConfig);
    }
  }, [filters.establishmentType, filters.company, filters.associateCompany, filters.location, filters.year, filters.month,
      filters.employer, filters.employerLocation, filters.subContractor, filters.subContractorLocation]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(filters.company, companies);
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(filters.associateCompany, associateCompanies);
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split('^')[0];
      const selectedLocationName = getEmployeeUploadLocationById(selectedLocationId, locations);
      setLocationName(selectedLocationName);
      const tempLocation = locations.find((loc: any) => loc.location_id === selectedLocationId) || {};
      setCompanyLocationMappingId(tempLocation.id || '');
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [filters.establishmentType, filters.company, filters.associateCompany, filters.location]);

  // Handler functions
  const handleSearch = () => {
    fetchData(getEmployeesWage, filters, searchConfig);
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: '' });
    fetchData(getEmployeesWage, filters, { ...searchConfig, searchInput: '' });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection = searchConfig.activeSort === sortField && searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection
    });
    fetchData(getEmployeesWage, filters, searchConfig, {}, sortField, newSortDirection);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployeesWage, filters, { ...searchConfig, page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0
    });
    fetchData(getEmployeesWage, filters, { ...searchConfig, rowsPerPage: newRowsPerPage, page: 0 });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || '',
      employerLocation: filterData.payload.employer_location_id_eq || '',
      subContractor: filterData.payload.sub_contractor_id_eq || '',
      subContractorLocation: filterData.payload.sub_contractor_location_id_eq || ''
    });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };

  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      { monthFieldName: 'month_eq', searchFieldName: 'employee_code_cont' }
    );
    exportRecords(payload);
  };

  useEffect(() => {
    if (employeesWageDetails.status === 'succeeded') {

    } else if (employeesWageDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeesWageDetails.status]);

  const onClickSortCode = () => handleSort('employee_code');
  const onClickSortNoOfDays = () => handleSort('no_of_days');
  const onClickSortHouseRent = () => handleSort('house_rent_allowance');

  const formatDate = (dateString: string | number | Date) => {
    if (!dateString) {
      return '-';
    }
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-GB', options);
  };

  return (
    <div style={{ height: '100%', backgroundColor: '#ffffff' }}>
      {loading ? <PageLoader>Loading...</PageLoader> :
        <div style={{ height: '100%' }}>
          <Box sx={{ paddingX: '20px', paddingY: '10px' }}>
            <div style={{ backgroundColor: '#E2E3F8', padding: '20px', borderRadius: '6px', boxShadow: '0px 6px 10px #CDD2D9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', marginTop: '10px' }}>
                <h5 style={{ font: 'normal normal normal 32px/40px Calibri' }}>Employee Wage</h5>
                <div style={{ marginRight: '12px', display: 'flex', alignItems: 'center', width: '200px', justifyContent: 'space-between' }}>
                  <button onClick={onClickExport} disabled={!filters.company || !filters.associateCompany} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: !filters.company || !filters.associateCompany ? '#707070' : '#ffffff', color: !filters.company || !filters.associateCompany ? '#ffffff' : '#000000', border: '1px solid #000000', width: '40px', height: '30px', borderRadius: '8px' }}> <FaDownload /> </button>
                </div>
              </div>
              <EmployeeUploadFilters
                filters={filters}
                searchConfig={searchConfig}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                onUpdateSearchConfig={updateSearchConfig}
                onSearch={handleSearch}
                onClearSearch={handleClearSearch}
                showYearMonth={true}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if (filters.company && filters.associateCompany && filters.location) {
                    setShowAdditionalFiltersModal(true)
                  } else {
                    toast.error('Please select Company, Associate Company and Location to apply additional filters')
                  }
                }}
                searchLabel="Search (Employee Code)"
                searchPlaceholder="Search"
              />
            </div>
          </Box>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

          <Box sx={{ paddingX: '20px', height: 'calc(100% - 215px)' }}>
            {
              employeesWage && employeesWage.length <= 0 ?
                <Box sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant='h5'>No Records Found</Typography>
                </Box>
                :
                <>
                  <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', height: '90%', overflowY: 'scroll' }}>
                    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                      <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7', fontWeight: '600' } }}>
                        <TableRow>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employee_code'} direction={searchConfig.sortType} onClick={onClickSortCode}>Employee Code</TableSortLabel></TableCell>
                          {filters.establishmentType == "CLRA" && <TableCell >Company Employer Sub-Contractor Code</TableCell>}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'no_of_days'} direction={searchConfig.sortType} onClick={onClickSortNoOfDays}>Month</TableSortLabel></TableCell>
                          <TableCell >Present Days</TableCell>
                          <TableCell >WorkDays</TableCell>
                          <TableCell >Basic Wages</TableCell>
                          <TableCell >Basic Arrear</TableCell>
                          <TableCell >Fixed Gross</TableCell>
                          <TableCell >Gross Deduction</TableCell>
                          <TableCell >Gross Ded Arrear</TableCell>
                          <TableCell >DA</TableCell>
                          <TableCell >DA Arrear</TableCell>
                          <TableCell >Gross Earnings</TableCell>
                          <TableCell >Gross Earnings Arrear</TableCell>
                          <TableCell >Date Of Payment</TableCell>
                          <TableCell >ESi </TableCell>
                          <TableCell >ESi Arrear</TableCell>
                          <TableCell >Esi Date Of Payment</TableCell>
                          <TableCell >Esi Date of Notice</TableCell>
                          <TableCell >ESi Time of Notice</TableCell>
                          <TableCell >Esi Cause</TableCell>
                          <TableCell >Esi Nature</TableCell>
                          <TableCell >Esi Date</TableCell>
                          <TableCell >Esi Time</TableCell>
                          <TableCell >Esi Place</TableCell>
                          <TableCell >Conveyance Allo. </TableCell>
                          <TableCell >Conveyance Arrear</TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'house_rent_allowance'} direction={searchConfig.sortType} onClick={onClickSortHouseRent}>HRA</TableSortLabel> </TableCell>
                          <TableCell >HRA Arrear</TableCell>
                          <TableCell >Spl Allow </TableCell>
                          <TableCell >Spl Allow Arrear</TableCell>
                          <TableCell >Insurance </TableCell>
                          <TableCell >Income Tax </TableCell>
                          <TableCell >Fines </TableCell>
                          <TableCell >Bonus </TableCell>
                          <TableCell >OverTime </TableCell>
                          <TableCell >OverTime Hrs</TableCell>
                          <TableCell >ProvidentFund </TableCell>
                          <TableCell >Pf Arrear</TableCell>
                          <TableCell >Professional Tax </TableCell>
                          <TableCell >Prof Tax Arrear</TableCell>
                          <TableCell >Medical Allowance </TableCell>
                          <TableCell >Net Payable </TableCell>
                          <TableCell >Net Payable Arrear </TableCell>
                          <TableCell >OverTime Taken Date</TableCell>
                          <TableCell >Maternity Leave From Date</TableCell>
                          <TableCell >DOB Of Child</TableCell>
                          <TableCell >Maternity Paid Date</TableCell>
                          <TableCell >Maternity Advance Paid</TableCell>
                          <TableCell >Maternity Bonus</TableCell>
                          <TableCell >Cause Of Damage or Loss Date</TableCell>
                          <TableCell >Date Of Deduction Show Cause</TableCell>
                          <TableCell >Damage Loss Amount</TableCell>
                          <TableCell >No.Of Ded Installments</TableCell>
                          <TableCell >Date On which Ded Competed</TableCell>
                          <TableCell >Salary Adv Paid Date</TableCell>
                          <TableCell >Salary Advance</TableCell>
                          <TableCell >No.Of Adv Installments to be recovered</TableCell>
                          <TableCell >Date on Adv Recovery Completed</TableCell>
                          <TableCell >Nature & Date for Fines</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employeesWage && employeesWage.map((each: any, index: number) => (
                          <TableRow
                            key={each._id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell >{each.employee_code}</TableCell>
                            {filters.establishmentType == "CLRA" && <TableCell >{(each.company_employer_sub_contractor || {}).company_employer_sub_contractor_code}</TableCell>}
                            <TableCell >{each.month}</TableCell>
                            <TableCell >{each.present_days}</TableCell>
                            <TableCell >{each.work_days}</TableCell>
                            <TableCell >{each.basic_wages}</TableCell>
                            <TableCell >{each.basic_arrear}</TableCell>
                            <TableCell >{each.fixed_gross}</TableCell>
                            <TableCell >{each.gross_deduction}</TableCell>
                            <TableCell >{each.gross_deduction_arrear}</TableCell>
                            <TableCell >{each.dearness_allowance}</TableCell>
                            <TableCell >{each.dearness_arrear}</TableCell>
                            <TableCell >{each.gross_earnings}</TableCell>
                            <TableCell >{each.gross_earnings_arrear}</TableCell>
                            <TableCell >{formatDate(each.date_of_payment)}</TableCell>
                            <TableCell >{each.esi}</TableCell>
                            <TableCell >{each.esi_arrear}</TableCell>
                            <TableCell >{formatDate(each.esi_date_of_payment)}</TableCell>
                            <TableCell >{formatDate(each.esi_date_of_notice)}</TableCell>
                            <TableCell >{each.esi_time_of_notice}</TableCell>
                            <TableCell >{each.esi_cause}</TableCell>
                            <TableCell >{each.esi_nature}</TableCell>
                            <TableCell >{formatDate(each.esi_date)}</TableCell>
                            <TableCell >{each.esi_time}</TableCell>
                            <TableCell >{each.esi_place}</TableCell>
                            <TableCell >{each.conveyance_allowance}</TableCell>
                            <TableCell >{each.conveyance_arreat}</TableCell>
                            <TableCell >{each.house_rent_allowance}</TableCell>
                            <TableCell >{each.house_rent_arrear}</TableCell>
                            <TableCell >{each.special_allowance}</TableCell>
                            <TableCell >{each.special_arrear}</TableCell>
                            <TableCell >{each.insurance}</TableCell>
                            <TableCell >{each.income_tax}</TableCell>
                            <TableCell >{each.fines}</TableCell>
                            <TableCell >{each.bonus}</TableCell>
                            <TableCell >{each.overtime}</TableCell>
                            <TableCell >{each.overtime_hours}</TableCell>
                            <TableCell >{each.provident_fund}</TableCell>
                            <TableCell >{each.provident_fund_arrear}</TableCell>
                            <TableCell >{each.professional_tax}</TableCell>
                            <TableCell >{each.profession_tax_arrear}</TableCell>
                            <TableCell >{each.medical_allowance}</TableCell>
                            <TableCell >{each.net_payable}</TableCell>
                            <TableCell >{each.net_payable_arrear}</TableCell>
                            <TableCell >{each.over_time_taken_date}</TableCell>
                            <TableCell >{formatDate(each.maternity_leave_from_date)}</TableCell>
                            <TableCell >{formatDate(each.date_of_birth_ofchild)}</TableCell>
                            <TableCell >{formatDate(each.maternity_paid_date)}</TableCell>
                            <TableCell >{each.maternity_advance_paid}</TableCell>
                            <TableCell >{each.maternity_bonus}</TableCell>
                            <TableCell >{formatDate(each.cause_of_damage_or_loss_date)}</TableCell>
                            <TableCell >{formatDate(each.date_of_deduction_show_cause)}</TableCell>
                            <TableCell >{each.damage_loss_amount}</TableCell>
                            <TableCell >{each.noof_deduction_installment_to_be_recovered}</TableCell>
                            <TableCell >{formatDate(each.dateon_which_deduction_completed)}</TableCell>
                            <TableCell >{formatDate(each.advance_paid_date)}</TableCell>
                            <TableCell >{each.salary_advance}</TableCell>
                            <TableCell >{each.no_of_adv_installments_to_be_recovered}</TableCell>
                            <TableCell >{formatDate(each.date_on_adv_recovery_completed)}</TableCell>
                            <TableCell >{each.nature_date_for_fine}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        backgroundColor: '#EFEBFE',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                      },
                      '.MuiTablePagination-displayedRows': {
                        margin: '0',
                      },
                      '.MuiTablePagination-selectLabel': {
                        margin: '0',
                      },
                      '.MuiTablePagination-spacer': {
                        display: 'none '
                      },
                      '.MuiTablePagination-input': {
                        marginRight: 'auto'
                      },
                    }}
                    labelRowsPerPage='Show'
                    labelDisplayedRows={(page) =>
                      `Showing ${page.from}-${page.to === -1 ? page.count : page.to} of ${page.count}`
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={employeesWageCount}
                    rowsPerPage={searchConfig.rowsPerPage}
                    page={searchConfig.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
            }
          </Box>
        </div>
      }
    </div>
  )
}

export default EmployeeWageUpload